import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '@/config/api';

const SiteGateContext = createContext(null);

export const useSiteGate = () => {
  const context = useContext(SiteGateContext);
  if (!context) {
    throw new Error('useSiteGate must be used within SiteGateProvider');
  }
  return context;
};

export const SiteGateProvider = ({ children }) => {
  const [enabled, setEnabled] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  const checkStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/site-gate/status`, {
        withCredentials: true,
      });
      setEnabled(response.data.enabled);
      setAuthenticated(response.data.authenticated);
    } catch (error) {
      // Fail closed: if gate status cannot be verified, keep the site locked.
      setEnabled(true);
      setAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  const login = async (email, password) => {
    await axios.post(
      `${API_BASE_URL}/api/site-gate/login`,
      { email, password },
      { withCredentials: true }
    );
    setAuthenticated(true);
  };

  const logout = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/site-gate/logout`, {}, {
        withCredentials: true,
      });
    } catch (error) {
      console.error('Site gate logout error:', error);
    }
    setAuthenticated(false);
  };

  return (
    <SiteGateContext.Provider
      value={{
        enabled,
        authenticated,
        loading,
        login,
        logout,
        checkStatus,
      }}
    >
      {children}
    </SiteGateContext.Provider>
  );
};

export default SiteGateContext;
